import { Alert, DialogContent, DialogContentText } from '@mui/material'
import React from 'react'
import { DialogWrapper } from '~components/dialog-wrapper'
import CircularProgress from '@mui/material/CircularProgress'
import {
  DocumentPreviewContent,
  isPreviewableExtension,
} from '~components/molecules/document-preview-content'
import { useGetDocument } from '~hooks/queries/documents/use-get-document'
import useBreakpoint from '~hooks/use-breakpoint'
import { getFileExtension } from './document'

interface DocumentPreviewDialogProps {
  onClose: () => void
  documentId: string
}

export const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  onClose,
  documentId,
}) => {
  const { isMobileSize } = useBreakpoint()

  const { refetch: fetchDownloadLink } = useGetDocument({
    documentId: documentId,
    attachment: true,
    disabled: true,
  })

  const { isLoading, data, isError } = useGetDocument({
    documentId: documentId,
    attachment: false,
  })

  if (isLoading) {
    return (
      <DialogWrapper
        onClose={onClose}
        onDownload={downloadDocument}
        showFullScreen
      >
        <CircularProgress aria-label="Loading…" />
      </DialogWrapper>
    )
  }

  if (isError || !data?.signedUrl) {
    return (
      <DialogWrapper onClose={onClose} onDownload={downloadDocument}>
        <DialogContent>
          <DialogContentText>
            <Alert severity="error">
              An error occurred while fetching the document preview. Please try
              downloading the document instead.
            </Alert>
          </DialogContentText>
        </DialogContent>
      </DialogWrapper>
    )
  }

  async function downloadDocument() {
    const { data } = await fetchDownloadLink()
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const documentExtension = getFileExtension(data.name)

  const showFullScreen = isPreviewableExtension(documentExtension)

  return (
    <DialogWrapper
      onClose={onClose}
      showFullScreen={showFullScreen}
      title={data.title}
      onDownload={downloadDocument}
    >
      <DocumentPreviewContent
        url={data.signedUrl}
        fileName={data.title}
        fileExtension={documentExtension}
        isMobileSize={isMobileSize}
      />
    </DialogWrapper>
  )
}
